import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronUpDownIcon, SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import useFermetureExterieure from '../../hooks/useFermetureExterieure';
import './UserMenu.css';
import { usePrivateTheme } from '../common/PrivateUIProvider';

/**
 * Menu utilisateur en pied de barre latérale : un déclencheur (avatar, nom,
 * sous-titre) qui ouvre un menu déroulant vers le haut.
 *
 * Générique : les entrées sont décrites par des données, aucune action n'est
 * codée en dur dans le composant.
 *
 * @param {string} nom - Libellé principal (nom ou e-mail)
 * @param {string} [email] - Affiché dans l'en-tête du menu
 * @param {string} initiales - Contenu de l'avatar
 * @param {React.ReactNode} [sousTitre] - Ligne sous le nom dans le déclencheur (ex. plan)
 * @param {Array<Array<Object>>} sections - Groupes d'entrées séparés par un filet.
 *   Entrée : `{ label, icon, href }` (lien) ou `{ label, icon, onClick }` (action),
 *   `danger: true` pour une action destructive.
 * @param {boolean} [compact=false] - Mode réduit : avatar seul, menu ouvert à droite
 * @param {Function} [onNavigate] - Appelé après chaque choix (ex. fermer le drawer mobile)
 */
const UserMenu = ({ nom, email, initiales, sousTitre, sections, compact = false, onNavigate }) => {
  // Le contexte privé fournit le choix d’apparence sans modifier les entrées du compte.
  const appearance = usePrivateTheme();
  const [estOuvert, setEstOuvert] = useState(false);
  const conteneurRef = useRef(null);

  // Fermeture au clic extérieur et à la touche Échap
  const fermer = useCallback(() => setEstOuvert(false), []);
  useFermetureExterieure(conteneurRef, estOuvert, fermer);

  // Ferme le menu puis prévient le parent après un choix
  const apresChoix = () => {
    setEstOuvert(false);
    onNavigate?.();
  };

  /**
   * Rend une entrée du menu : lien interne si `href`, bouton sinon.
   * @param {Object} entree - Entrée décrite dans `sections`
   */
  const renderEntree = (entree) => {
    const classes = `user-menu__item dashboard-focusable ${entree.danger ? 'user-menu__item--danger' : ''}`;
    const contenu = (
      <>
        <entree.icon aria-hidden="true" className="user-menu__item-icon" />
        <span>{entree.label}</span>
      </>
    );

    return entree.href ? (
      <Link key={entree.label} to={entree.href} role="menuitem" className={classes} onClick={apresChoix}>
        {contenu}
      </Link>
    ) : (
      <button
        key={entree.label}
        type="button"
        role="menuitem"
        className={classes}
        onClick={() => { apresChoix(); entree.onClick?.(); }}
      >
        {contenu}
      </button>
    );
  };

  return (
    <div ref={conteneurRef} className={`user-menu ${compact ? 'user-menu--compact' : ''}`}>
      {/* Menu déroulant (au-dessus du déclencheur, ou à sa droite en mode réduit) */}
      {estOuvert && (
        <div className="user-menu__panel" role="menu" aria-label="Menu du compte">
          <div className="user-menu__header">
            <p className="user-menu__header-name">{nom}</p>
            {email && email !== nom && <p className="user-menu__header-sub">{email}</p>}
          </div>
          {/* Le thème effectif indique le bouton actif ; un clic mémorise le choix explicite sans fermer le menu. */}
          {appearance && <div className="user-menu__section private-theme-picker">
            <span className="private-theme-label"><SunIcon aria-hidden="true" />Thème</span>
            <div className="private-theme-buttons" role="group" aria-label="Thème">
              <button type="button" aria-label="Thème clair" title="Thème clair" aria-pressed={appearance.mode === 'light'} onClick={() => appearance.changePreference('light')}><SunIcon aria-hidden="true" /></button>
              <button type="button" aria-label="Thème sombre" title="Thème sombre" aria-pressed={appearance.mode === 'dark'} onClick={() => appearance.changePreference('dark')}><MoonIcon aria-hidden="true" /></button>
            </div>
          </div>}
          {sections.map((entrees, index) => (
            <div key={index} className="user-menu__section">
              {entrees.map(renderEntree)}
            </div>
          ))}
        </div>
      )}

      {/* Déclencheur : avatar + nom + sous-titre (masqués en mode réduit) */}
      <button
        type="button"
        className="user-menu__trigger dashboard-focusable"
        onClick={() => setEstOuvert((precedent) => !precedent)}
        aria-haspopup="menu"
        aria-expanded={estOuvert}
        aria-label={compact ? `Menu du compte : ${nom}` : undefined}
        title={compact ? nom : undefined}
      >
        <span className="dashboard-avatar user-menu__avatar" aria-hidden="true">{initiales}</span>
        {!compact && (
          <>
            <span className="user-menu__text">
              <span className="user-menu__name">{nom}</span>
              {sousTitre && <span className="user-menu__sub">{sousTitre}</span>}
            </span>
            <ChevronUpDownIcon aria-hidden="true" className="user-menu__chevron" />
          </>
        )}
      </button>
    </div>
  );
};

export default UserMenu;
